import React, { useEffect, useMemo, useState } from "react";
import { Column, FilterSpec, TableFeatures } from "./types";
import { useTableState } from "./state/useTableState";
import { applyFilters } from "./engine/filterEngine";
import { applySorts } from "./engine/sortEngine";
import { buildPivotRows } from "./engine/pivotEngine";
import { TableRoot } from "./ui/TableRoot";
import { TableHeader } from "./ui/TableHeader";
import { TableBody } from "./ui/TableBody";
import { TableFooter } from "./ui/TableFooter";
import { ActiveFiltersBar } from "./ui/ActiveFiltersBar";
import { ColumnReorderBar } from "./ui/ColumnReorderBar";
import { VirtualBodyAdapter } from "./ui/VirtualBodyAdapter";
import { Dropdown } from "../Dropdown";
import { ExcelExport } from "./tools/ExcelExport";

const normalizeColumns = <T,>(cols: Column<T>[]) => {
  return cols.map((c) => ({
    sortable: c.sortable ?? true,
    filterable: c.filterable ?? true,
    hideable: c.hideable ?? true,
    resizable: c.resizable ?? false,
    pinnable: c.pinnable ?? false,
    minWidth: c.minWidth ?? 140,
    ...c,
  })) as Column<T>[];
};

const uniq = (arr: string[]) => {
  const set = new Set<string>();
  const out: string[] = [];
  for (const a of arr) {
    if (set.has(a)) continue;
    set.add(a);
    out.push(a);
  }
  return out;
};

export const Table = <T,>({
  tableKey,
  columns,
  data,
  features,
  initialState,
  state,
  onStateChange,
  getRowKey,
  emptyText,
}: any) => {
  const f: TableFeatures = {
    sortable: true,
    filterable: true,
    columnReorder: true,
    columnHideShow: true,
    pivot: true,
    pagination: true,
    persistence: true,
    excel: true,
    ...features,
  };

  const cols = useMemo(() => normalizeColumns(columns), [columns]);

  const { state: st, setState } = useTableState({
    tableKey,
    initialState: {
      ...initialState,
      columnOrder: initialState?.columnOrder?.length
        ? initialState.columnOrder
        : cols.map((c) => c.key),
    },
    state,
    onStateChange,
    persistence: !!f.persistence,
  });

  useEffect(() => {
    const defined = new Set(cols.map((c) => c.key));
    const cleaned = (st.columnOrder || []).filter((k) => defined.has(k));
    const missing = cols.map((c) => c.key).filter((k) => !cleaned.includes(k));
    const next = uniq([...cleaned, ...missing]);
    if (next.join("|") !== (st.columnOrder || []).join("|")) {
      setState({ columnOrder: next });
    }
  }, [cols.map((c) => c.key).join("|")]);

  const colMap = useMemo(() => new Map(cols.map((c) => [c.key, c])), [cols]);

  const orderedColumns = useMemo(() => {
    const arr: Column<T>[] = [];
    for (const k of st.columnOrder || []) {
      const c = colMap.get(k);
      if (c) arr.push(c);
    }
    for (const c of cols) if (!arr.find((x) => x.key === c.key)) arr.push(c);
    return arr;
  }, [cols, st.columnOrder?.join("|")]);

  const visibleColumns = useMemo(() => {
    return orderedColumns.filter((c) => !st.columnHidden?.[c.key]);
  }, [orderedColumns, JSON.stringify(st.columnHidden || {})]);

  const rowsFiltered = useMemo(() => {
    return f.filterable ? applyFilters(data, cols, st.filters || []) : data;
  }, [data, cols, JSON.stringify(st.filters || []), f.filterable]);

  const rowsSorted = useMemo(() => {
    return f.sortable
      ? applySorts(rowsFiltered, cols, st.sorts || [])
      : rowsFiltered;
  }, [rowsFiltered, cols, JSON.stringify(st.sorts || []), f.sortable]);

  const pivotBy = useMemo(() => {
    const p: any = st.pivot;
    if (!p) return "";
    if (typeof p === "object" && typeof p.by === "string") return p.by;
    return "";
  }, [JSON.stringify(st.pivot || {})]);

  const pivotCollapsed = useMemo(() => {
    const p: any = st.pivot;
    if (!p) return {};
    if (typeof p === "object" && p.collapsed && typeof p.collapsed === "object")
      return p.collapsed;
    return {};
  }, [JSON.stringify(st.pivot || {})]);

  const pivotActive = !!(f.pivot && pivotBy);

  const pivotRows = useMemo(() => {
    if (!pivotActive) return rowsSorted as any[];
    const col = cols.find((c) => c.key === pivotBy);
    if (!col) return rowsSorted as any[];
    return buildPivotRows(
      rowsSorted as any[],
      {
        groupBy: (row: any) => {
          const v = col.accessor ? col.accessor(row) : row[pivotBy];
          return String(v ?? "—");
        },
        label: (key: string) => key,
      },
      pivotCollapsed
    );
  }, [rowsSorted, cols, pivotBy, pivotActive, JSON.stringify(pivotCollapsed)]);

  const totalRows = useMemo(() => {
    return pivotActive
      ? (pivotRows as any[]).filter((x) => x?.kind === "row").length
      : rowsSorted.length;
  }, [pivotRows, rowsSorted, pivotActive]);

  const pageRows = useMemo(() => {
    if (!f.pagination) return pivotRows;
    const start = st.pagination.page * st.pagination.pageSize;
    const end = start + st.pagination.pageSize;
    if (!pivotActive) return (rowsSorted as any).slice(start, end);
    const out: any[] = [];
    let rowCount = 0;
    for (const r of pivotRows as any[]) {
      if (r?.kind === "row") {
        if (rowCount >= start && rowCount < end) out.push(r);
        rowCount++;
      } else out.push(r);
    }
    return out;
  }, [
    pivotRows,
    rowsSorted,
    st.pagination.page,
    st.pagination.pageSize,
    f.pagination,
    pivotActive,
  ]);

  const keyFn = useMemo(() => {
    if (typeof getRowKey === "function") return getRowKey;
    return (row: any, index: number) => {
      if (row?.kind === "row") {
        const r = row.row;
        if (r?.id || r?.key) return String(r.id || r.key);
        return `r:${index}`;
      }
      if (row?.kind === "group") return `g:${row.group.key}:${index}`;
      if (row?.id || row?.key) return String(row.id || row.key);
      return `r:${index}`;
    };
  }, [getRowKey]);

  const removeFilter = (key: string) => {
    setState((s) => ({
      filters: (s.filters || []).filter((fx) => fx.key !== key),
      pagination: { ...s.pagination, page: 0 },
    }));
  };

  const clearFilters = () => {
    setState((s) => ({
      filters: [],
      pagination: { ...s.pagination, page: 0 },
    }));
  };

  const toggleSort = (key: string) => {
    setState((s) => {
      const list = [...(s.sorts || [])];
      const ex = list.find((x) => x.key === key);
      if (!ex) list.push({ key, dir: "asc", order: list.length + 1 });
      else if (ex.dir === "asc") ex.dir = "desc";
      else return { sorts: list.filter((x) => x.key !== key) };
      return { sorts: list, pagination: { ...s.pagination, page: 0 } };
    });
  };

  const [activeFilterKey, setActiveFilterKey] = useState<string | null>(null);

  useEffect(() => {
    const close = () => setActiveFilterKey(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  const changeFilter = (next: FilterSpec | null) => {
    setState((s) => {
      const list = [...(s.filters || [])].filter(
        (fx) => fx.key !== activeFilterKey
      );
      if (next) list.push(next);
      return { filters: list, pagination: { ...s.pagination, page: 0 } };
    });
    setActiveFilterKey(null);
  };

  const toggleHidden = (key: string) => {
    setState((s) => ({
      columnHidden: {
        ...(s.columnHidden || {}),
        [key]: !s.columnHidden?.[key],
      },
    }));
  };

  const reorder = (fromKey: string, toKey: string) => {
    setState((s) => {
      const currentOrder = [...(s.columnOrder || [])];
      const visible = orderedColumns.map((c) => c.key);
      const fromVisibleIndex = visible.indexOf(fromKey);
      const toVisibleIndex = visible.indexOf(toKey);
      if (fromVisibleIndex === -1 || toVisibleIndex === -1) return {};
      const nextVisible = [...visible];
      nextVisible.splice(fromVisibleIndex, 1);
      nextVisible.splice(toVisibleIndex, 0, fromKey);
      const rest = currentOrder.filter((k) => !nextVisible.includes(k));
      return { columnOrder: [...nextVisible, ...rest] };
    });
  };

  const togglePivotGroup = (key: string) => {
    setState((s) => ({
      pivot: {
        by: pivotBy,
        collapsed: {
          ...(pivotCollapsed || {}),
          [key]: !(pivotCollapsed || {})[key],
        },
      },
    }));
  };

  const setPivotBy = (by: string) => {
    setState((s) => ({
      pivot: by ? { by, collapsed: {} } : undefined,
      pagination: { ...s.pagination, page: 0 },
    }));
  };

  return (
    <TableRoot>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          gap: 12,
        }}
      >
        {f.pivot && (
          <div style={{ width: 240 }}>
            <Dropdown
              searchable
              value={pivotBy}
              placeholder="Select pivot column"
              items={[
                { value: "", label: "Pivot Close" },
                ...visibleColumns.map((c) => ({
                  value: c.key,
                  label: (c as any).title || c.key,
                })),
              ]}
              onSelect={(item: any) => setPivotBy(item.value)}
            />
          </div>
        )}
        {f.excel && (
          <ExcelExport
            columns={visibleColumns.map((c) => ({
              key: c.key,
              label: c.title,
            }))}
            data={data}
            fileName="table_export"
          />
        )}
      </div>

      {f.columnReorder && (
        <ColumnReorderBar
          columns={orderedColumns as any}
          visibleColumns={visibleColumns as any}
          data={data}
          onReorder={reorder}
        />
      )}

      <ActiveFiltersBar
        filters={st.filters || []}
        onRemove={removeFilter}
        onClear={clearFilters}
      />

      <VirtualBodyAdapter>
        <table
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
            width: "max-content",
            minWidth: "100%",
          }}
        >
          <TableHeader
            columns={cols as any}
            visibleColumns={visibleColumns as any}
            sorts={st.sorts || []}
            filters={st.filters || []}
            onToggleSort={toggleSort}
            onToggleHidden={toggleHidden}
            hiddenMap={st.columnHidden || {}}
            onOpenFilter={(key: string) => setActiveFilterKey(key)}
            activeFilterKey={activeFilterKey}
            onFilterChange={changeFilter}
            onCloseFilter={() => setActiveFilterKey(null)}
            data={data}
          />

          {pageRows.length === 0 ? (
            <tbody>
              <tr>
                <td
                  colSpan={(visibleColumns?.length || 0) + 1}
                  style={{ padding: 18, opacity: 0.7 }}
                >
                  {emptyText || "No data"}
                </td>
              </tr>
            </tbody>
          ) : (
            <TableBody
              columns={visibleColumns as any}
              rows={pageRows as any}
              getRowKey={keyFn as any}
              onToggleGroup={pivotActive ? togglePivotGroup : undefined}
            />
          )}
        </table>
      </VirtualBodyAdapter>

      {f.pagination && (
        <TableFooter
          totalRows={totalRows}
          pagination={st.pagination}
          onChange={(p: any) => setState({ pagination: p })}
        />
      )}
    </TableRoot>
  );
};
